import React, { FC, ReactNode, useState } from "react";
import { keyframes, styled } from "@mui/system";
import { COLORS, FONT_FAMILIES, FONT_WEIGHTS, RADII, SPACINGS } from "../../common/consts";
import Checkbox from "../DataInput/Checkbox";
import { BlockBone, PillBone, TextBone } from "../Display/Skeletons";
import { makeShouldForwardProps } from "../../common/utils/ui";
import { SettingsCaption } from "./index";
import { CollapseToggle } from "./Collapsible";
import { ReactComponent as RefreshIcon } from "../../assets/icons/refresh.svg";

/**
 * Outreach's own field-mapping screen (Admin > CRM > Salesforce > an object's
 * configuration), rebuilt for boring: a centred card, then a bordered table
 * whose header is a band tinted by the two systems at either end, and one row
 * per field with the source on the left, the sync pill in the middle and the
 * destination field on the right.
 */

export interface MappingSystem {
  name: string;
  icon: ReactNode;
  /** The brand colour, as "r, g, b", for the name and the band's tint. */
  rgb: string;
}

/* The three columns every row and the band share. */
const COLUMNS = "minmax(0, 1fr) 11.5rem minmax(0, 1fr)";

/* No box of its own: the table sits inside its section's box. */
const Card = styled("section")`
  width: 100%;
  box-sizing: border-box;
`;

/* The card's own heading, kept at 16px Work Sans. The shared SettingsTitle
   is a 14px sub-heading now that the other sections sit unboxed. */
const CardTitle = styled("h3")`
  font-family: ${FONT_FAMILIES.titles};
  font-size: 1rem;
  font-weight: ${FONT_WEIGHTS.semibold};
  line-height: 1.5rem;
  color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
  margin: 0;
`;

const CardHeader = styled("header")`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACINGS.lg};
  padding-bottom: ${SPACINGS.lg};
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const IconButton = styled("button", {
  shouldForwardProp: makeShouldForwardProps<{ spinning?: boolean }>(["spinning"]),
})<{ spinning?: boolean }>`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: ${RADII.full};
  background: transparent;
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    background-color: var(--color-backgroundSecondary, ${COLORS.backgroundSecondary});
    color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});
  }

  &:disabled {
    cursor: default;
  }

  svg {
    width: 18px;
    height: 18px;
    animation: ${({ spinning }) => (spinning ? `${spin} 0.9s linear infinite` : "none")};
  }
`;

const Table = styled("div")`
  border: 1.5px solid var(--color-backgroundBrand, ${COLORS.backgroundBrand});
  border-radius: ${RADII.md};
  overflow: hidden;
`;

const Band = styled("div", {
  shouldForwardProp: makeShouldForwardProps<{ from: string; to: string }>(["from", "to"]),
})<{ from: string; to: string }>`
  display: grid;
  grid-template-columns: ${COLUMNS};
  align-items: center;
  gap: ${SPACINGS.lg};
  min-height: 2.625rem;
  padding: 0 ${SPACINGS.xl};
  background-image: ${({ from, to }) =>
    `linear-gradient(90deg, rgba(${from}, 0.2) 0%, rgba(${from}, 0) 45%, rgba(${to}, 0) 50%, rgba(${to}, 0.2) 100%)`};

  @media (max-width: 700px) {
    grid-template-columns: 1fr 1fr;

    > :nth-child(2) {
      display: none;
    }
  }
`;

const Brand = styled("h6", {
  shouldForwardProp: makeShouldForwardProps<{ rgb: string; end?: boolean }>(["rgb", "end"]),
})<{ rgb: string; end?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ end }) => (end ? "flex-end" : "flex-start")};
  gap: ${SPACINGS.sm};
  margin: 0;
  font-family: ${FONT_FAMILIES.titles};
  font-size: 0.875rem;
  font-weight: ${FONT_WEIGHTS.semibold};
  color: ${({ rgb }) => `rgb(${rgb})`};

  svg {
    width: 20px;
    height: 20px;
    flex: 0 0 auto;
  }
`;

const ColumnHeader = styled("h6")`
  margin: 0;
  text-align: center;
  font-family: ${FONT_FAMILIES.titles};
  font-size: 0.8125rem;
  font-weight: ${FONT_WEIGHTS.semibold};
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
`;

/* The header's right side: any actions, then the reload button. */
const HeaderActions = styled("div")`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: ${SPACINGS.sm};
`;

/* Pickers that choose what the table shows, between the heading and the table. */
const Toolbar = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACINGS.md};
  padding-bottom: ${SPACINGS.lg};

  > * {
    flex: 0 1 17.5rem;
    min-width: 0;
  }
`;

export interface FieldMappingTableProps {
  title: ReactNode;
  description: ReactNode;
  /** Buttons at the right of the heading, such as "Reset to defaults". */
  actions?: ReactNode;
  /** Pickers above the table, such as the destination and record. */
  toolbar?: ReactNode;
  /** The middle column's heading. */
  middleLabel?: string;
  /** Where the values come from: boring. */
  source: MappingSystem;
  /** Where they are written: Outreach, or HubSpot. */
  destination: MappingSystem;
  /** Reloads the destination's field list; the button is left out without it. */
  onRefresh?: () => void;
  refreshing?: boolean;
  /** One FieldMappingRow per field. */
  children: ReactNode;
  /** Under the table, such as adding a field; hidden with it. */
  footer?: ReactNode;
  /** The heading opens and closes the table (the toolbar, rows and footer). */
  collapsible?: boolean;
  defaultOpen?: boolean;
}

export const FieldMappingTable: FC<FieldMappingTableProps> = ({
  title,
  description,
  source,
  destination,
  actions,
  toolbar,
  middleLabel = "Sync",
  onRefresh,
  refreshing = false,
  children,
  footer,
  collapsible = false,
  defaultOpen = true,
}) => {
  const [isOpen, setOpen] = useState(defaultOpen);
  const open = !collapsible || isOpen;
  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>
            {collapsible ? (
              <CollapseToggle open={open} onToggle={() => setOpen((o) => !o)}>
                {title}
              </CollapseToggle>
            ) : (
              title
            )}
          </CardTitle>
          <SettingsCaption>{description}</SettingsCaption>
        </div>
        {open && (actions || onRefresh) && (
          <HeaderActions>
            {actions}
            {onRefresh && (
              <IconButton
                type="button"
                onClick={onRefresh}
                disabled={refreshing}
                spinning={refreshing}
                aria-label={`Reload ${destination.name} fields`}
                title={`Reload ${destination.name} fields`}
              >
                <RefreshIcon />
              </IconButton>
            )}
          </HeaderActions>
        )}
      </CardHeader>
      {open && toolbar && <Toolbar>{toolbar}</Toolbar>}
      {open && (
        <Table role="table">
          <Band from={source.rgb} to={destination.rgb} role="row">
            <Brand rgb={source.rgb} role="columnheader">
              {source.icon}
              {source.name}
            </Brand>
            <ColumnHeader role="columnheader">{middleLabel}</ColumnHeader>
            <Brand rgb={destination.rgb} end role="columnheader">
              {destination.name}
              {destination.icon}
            </Brand>
          </Band>
          {children}
        </Table>
      )}
      {open && footer}
    </Card>
  );
};

const Row = styled("div")`
  display: grid;
  grid-template-columns: ${COLUMNS};
  align-items: center;
  gap: ${SPACINGS.lg};
  min-height: 4.5rem;
  padding: ${SPACINGS.md} ${SPACINGS.xl};
  box-sizing: border-box;
  border-top: 1.5px solid var(--color-backgroundBrand, ${COLORS.backgroundBrand});

  /* On a phone the three cells stack: field, pill, then the picker. */
  @media (max-width: 700px) {
    grid-template-columns: minmax(0, 1fr);
    row-gap: ${SPACINGS.sm};

    > :nth-child(2) {
      justify-content: flex-start;
    }

    > :nth-child(3) > * {
      max-width: none;
    }
  }
`;

const SourceCell = styled("label")`
  display: flex;
  align-items: center;
  gap: ${SPACINGS.md};
  min-width: 0;
  cursor: pointer;
`;

const SourceText = styled("span")`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const FieldName = styled("span")`
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
`;

const FieldDetail = styled("em")`
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.8125rem;
  line-height: 1.125rem;
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
`;

const PillCell = styled("div")`
  display: flex;
  justify-content: center;
`;

const Pill = styled("button", {
  shouldForwardProp: makeShouldForwardProps<{ on: boolean }>(["on"]),
})<{ on: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 2rem;
  padding: 0 ${SPACINGS.md};
  border-radius: 1rem;
  border: 1.5px solid
    ${({ on }) =>
      on
        ? `rgba(76, 161, 138, 0.4)`
        : `var(--color-backgroundBrand, ${COLORS.backgroundBrand})`};
  background-color: ${({ on }) =>
    on ? "rgba(76, 161, 138, 0.12)" : `var(--color-backgroundSecondary, ${COLORS.backgroundSecondary})`};
  color: ${({ on }) =>
    on
      ? `var(--color-foregroundPrimary, ${COLORS.foregroundPrimary})`
      : `var(--color-foregroundSecondary, ${COLORS.foregroundSecondary})`};
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.8125rem;
  font-weight: ${({ on }) => (on ? FONT_WEIGHTS.semibold : FONT_WEIGHTS.regular)};
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});
  }

  &:focus-visible {
    outline: 2px solid var(--color-foregroundBrand, ${COLORS.foregroundBrand});
    outline-offset: 2px;
  }

  svg {
    width: 14px;
    height: 14px;
    color: var(--color-integrationConnected, ${COLORS.integrationConnected});
  }
`;

/* Outreach right-aligns the chosen field in its dropdown cells. */
const DestinationCell = styled("div")`
  display: flex;
  justify-content: flex-end;
  min-width: 0;

  > * {
    width: 100%;
    max-width: 17.5rem;
  }

  .MuiSelect-select {
    text-align: right;
  }
`;

const Arrow: FC = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M2 8h11M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export interface FieldMappingRowProps {
  id: string;
  /** The boring field, in plain words. */
  label: ReactNode;
  /** What it writes, under the name: "Valid · Invalid · Questionable". */
  detail: ReactNode;
  /** A longer explanation, shown on hover. */
  hint?: string;
  syncing: boolean;
  onSyncingChange: (syncing: boolean) => void;
  /** Named in the pill: "Syncing to Outreach". */
  destinationName: string;
  /** The destination field picker, a Select. */
  children: ReactNode;
}

export const FieldMappingRow: FC<FieldMappingRowProps> = ({
  id,
  label,
  detail,
  hint,
  syncing,
  onSyncingChange,
  destinationName,
  children,
}) => (
  <Row role="row" title={hint}>
    <SourceCell htmlFor={`mapping-${id}`} role="cell">
      <Checkbox id={`mapping-${id}`} checked={syncing} onChange={onSyncingChange} />
      <SourceText>
        <FieldName>{label}</FieldName>
        <FieldDetail>{detail}</FieldDetail>
      </SourceText>
    </SourceCell>
    <PillCell role="cell">
      <Pill type="button" on={syncing} aria-pressed={syncing} onClick={() => onSyncingChange(!syncing)}>
        {syncing ? (
          <>
            Syncing to {destinationName}
            <Arrow />
          </>
        ) : (
          "Not syncing"
        )}
      </Pill>
    </PillCell>
    <DestinationCell role="cell">{children}</DestinationCell>
  </Row>
);

/* ------------------------------------------------------------------ */
/* A row whose source is typed in, for the export mapping: the profile */
/* field on the left, what happens in the middle, the field it lands   */
/* in on the right.                                                    */
/* ------------------------------------------------------------------ */

/* The same pill as the sync toggle, as a status that is not clickable. */
const StatusPill = styled("span", {
  shouldForwardProp: makeShouldForwardProps<{ on: boolean }>(["on"]),
})<{ on: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 2rem;
  padding: 0 ${SPACINGS.md};
  border-radius: 1rem;
  border: 1.5px solid
    ${({ on }) => (on ? "rgba(76, 161, 138, 0.4)" : `var(--color-backgroundBrand, ${COLORS.backgroundBrand})`)};
  background-color: ${({ on }) =>
    on ? "rgba(76, 161, 138, 0.12)" : `var(--color-backgroundSecondary, ${COLORS.backgroundSecondary})`};
  color: ${({ on }) =>
    on
      ? `var(--color-foregroundPrimary, ${COLORS.foregroundPrimary})`
      : `var(--color-foregroundSecondary, ${COLORS.foregroundSecondary})`};
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.8125rem;
  font-weight: ${({ on }) => (on ? FONT_WEIGHTS.semibold : FONT_WEIGHTS.regular)};
  white-space: nowrap;

  svg {
    width: 14px;
    height: 14px;
    color: var(--color-integrationConnected, ${COLORS.integrationConnected});
  }
`;

/* The typed-in source, as wide as the destination pickers are. */
const InputCell = styled("div")`
  min-width: 0;

  > * {
    width: 100%;
    max-width: 17.5rem;
  }
`;

/* The field it lands in, right-aligned like Outreach's picked fields. */
/* The destination column's picker, at the column's right edge like the field names. */
const TargetInputCell = styled(InputCell)`
  display: flex;
  justify-content: flex-end;
`;

const TargetCell = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
  min-width: 0;
`;

export type FieldMappingValueStatus = "writes" | "fixed" | "unmapped";

export interface FieldMappingValueRowProps {
  /** Where the value comes from, an Input. */
  source: ReactNode;
  status: FieldMappingValueStatus;
  /** Named in the pill: "Writes to Outreach". */
  destinationName: string;
  /** The destination field, in plain words. */
  label: ReactNode;
  /** Its API name, under the label. */
  detail?: ReactNode;
  /** A control under the field name, such as the admin's lock. */
  trailing?: ReactNode;
  /** A picker for the destination field, in place of its name. */
  target?: ReactNode;
}

export const FieldMappingValueRow: FC<FieldMappingValueRowProps> = ({
  source,
  status,
  destinationName,
  label,
  detail,
  trailing,
  target,
}) => (
  <Row role="row">
    <InputCell role="cell">{source}</InputCell>
    <PillCell role="cell">
      <StatusPill on={status !== "unmapped"}>
        {status === "writes" && (
          <>
            Writes to {destinationName}
            <Arrow />
          </>
        )}
        {status === "fixed" && (
          <>
            Always this text
            <Arrow />
          </>
        )}
        {status === "unmapped" && "Not mapped"}
      </StatusPill>
    </PillCell>
    {target ? (
      <TargetInputCell role="cell">{target}</TargetInputCell>
    ) : (
      <TargetCell role="cell">
        <FieldName>{label}</FieldName>
        {detail && <FieldDetail>{detail}</FieldDetail>}
        {trailing}
      </TargetCell>
    )}
  </Row>
);

const EmptyRow = styled("div")`
  padding: ${SPACINGS.lg} ${SPACINGS.xl};
  border-top: 1.5px solid var(--color-backgroundBrand, ${COLORS.backgroundBrand});
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
`;

const AddLabel = styled("span")`
  display: flex;
  align-items: center;
  gap: ${SPACINGS.sm};
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: ${FONT_WEIGHTS.semibold};
  color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
`;

/**
 * The table's last row: adds a field. The label sits where a row's source
 * does and the picker where its destination does, so it lines up with the
 * rows above; picking a field adds it.
 */
export const FieldMappingAddRow: FC<{ label: ReactNode; children: ReactNode }> = ({ label, children }) => (
  <Row role="row">
    <AddLabel role="cell">
      <span aria-hidden="true">+</span>
      {label}
    </AddLabel>
    <PillCell role="cell" />
    <TargetInputCell role="cell">{children}</TargetInputCell>
  </Row>
);

/** The table's only row when there is nothing to map. */
export const FieldMappingEmptyRow: FC<{ children: ReactNode }> = ({ children }) => (
  <EmptyRow role="row">{children}</EmptyRow>
);

/** A row while the table loads: field, pill, then field. */
export const FieldMappingRowSkeleton: FC = () => (
  <Row role="row">
    <BlockBone height={36} />
    <PillCell>
      <PillBone width={132} height={32} />
    </PillCell>
    <TargetCell>
      <TextBone width={120} size="0.875rem" />
    </TargetCell>
  </Row>
);
